import { Col, Input, Modal, Row, Select, Spin, Table, Tabs, message } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import UserFloatingButton from "pages/Performance/UserFloatingButton";
import userFloatCycle from "assets/userFloatCycle.svg";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .average-score-box {
    display: inline-block;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 6px 16px;
    font-weight: 600;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
`;

const { TextArea } = Input;

const SCORE_CLASSIFICATION = [
  { label: "Far Exceeds Expectations", value: 5, color: "#22c55e" },
  { label: "Exceeds Expectations (EE)", value: 4, color: "#86efac" },
  { label: "Meets Expectations (ME)", value: 3, color: "#fde047" },
  { label: "Below Expectations (BE)", value: 2, color: "#fb923c" },
  { label: "Far Below Expectations (FBE)", value: 1, color: "#ef4444" },
];

const SCORE_OPTIONS = SCORE_CLASSIFICATION.map((item) => ({
  label: item.value,
  value: item.value,
})).sort((a, b) => a.value - b.value);

// While the appraisee is still attending (not yet closed), the appraiser
// hasn't reviewed it yet - showing their comment column here would be
// premature (and empty).
const buildTrainingColumns = (isClosed) => [
  { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
  { title: "Planned areas for strengthening", dataIndex: "skill", key: "skill" },
  {
    title: "Method of development",
    dataIndex: "method",
    key: "method",
  },
  { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 160 },
  {
    title: "Development / training taken (also indicate method & place)",
    dataIndex: "taken",
    key: "taken",
    render: (val) => val || "-",
  },
  ...(isClosed
    ? [
        {
          title: "Appraiser Comment",
          dataIndex: "appraisertrainingcomment",
          key: "appraisertrainingcomment",
          render: (val) => val || "-",
        },
      ]
    : []),
];

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const rowKeyOf = (perspective, sno) => `${perspective}-${sno}`;

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

const AttendContract = () => {
  const history = useHistory();
  const location = useLocation();
  const appraisalid = location.state?.appraisalid;

  const [appraisal, setAppraisal] = useState(null);
  const [resultsMap, setResultsMap] = useState({});
  const [scoreMap, setScoreMap] = useState({});
  const [softSkillsWentWellMap, setSoftSkillsWentWellMap] = useState({});
  const [softSkillsImprovedMap, setSoftSkillsImprovedMap] = useState({});
  const [softSkillsScoreMap, setSoftSkillsScoreMap] = useState({});
  const [appraiseecomment, setAppraiseecomment] = useState("");
  const [appraiserScoreMap, setAppraiserScoreMap] = useState({});
  const [agreedScoreMap, setAgreedScoreMap] = useState({});
  const [appraiserRemarksMap, setAppraiserRemarksMap] = useState({});
  const [softAppraiserScoreMap, setSoftAppraiserScoreMap] = useState({});
  const [softAgreedScoreMap, setSoftAgreedScoreMap] = useState({});
  const [softAppraiserCommentMap, setSoftAppraiserCommentMap] = useState({});
  const [appraisercomment, setAppraisercomment] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [kpiViewOpen, setKpiViewOpen] = useState(false);
  const [activeReviewTab, setActiveReviewTab] = useState("planning");

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  const fetchAppraisal = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/appraisal/${appraisalid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setAppraisal(data);
        const initialResults = {};
        const initialScores = {};
        const initialAppraiserScores = {};
        const initialAgreedScores = {};
        const initialAppraiserRemarks = {};
        (data?.scoring || []).forEach((item) => {
          const key = rowKeyOf(item.perspective, item.sno);
          initialResults[key] = item.performanceresults;
          initialScores[key] = item.selfscore;
          initialAppraiserScores[key] = item.appraiserscore;
          initialAgreedScores[key] = item.agreedscore;
          initialAppraiserRemarks[key] = item.appraiserremarks;
        });
        setResultsMap(initialResults);
        setScoreMap(initialScores);
        setAppraiserScoreMap(initialAppraiserScores);
        setAgreedScoreMap(initialAgreedScores);
        setAppraiserRemarksMap(initialAppraiserRemarks);

        const initialWentWell = {};
        const initialImproved = {};
        const initialSoftScores = {};
        const initialSoftAppraiserScores = {};
        const initialSoftAgreedScores = {};
        const initialSoftAppraiserComments = {};
        (data?.softskillsscoring || []).forEach((item) => {
          initialWentWell[item.objectives] = item.wentwell;
          initialImproved[item.objectives] = item.improved;
          initialSoftScores[item.objectives] = item.selfscore;
          initialSoftAppraiserScores[item.objectives] = item.appraiserscore;
          initialSoftAgreedScores[item.objectives] = item.agreedscore;
          initialSoftAppraiserComments[item.objectives] = item.appraisercomment;
        });
        setSoftSkillsWentWellMap(initialWentWell);
        setSoftSkillsImprovedMap(initialImproved);
        setSoftSkillsScoreMap(initialSoftScores);
        setSoftAppraiserScoreMap(initialSoftAppraiserScores);
        setSoftAgreedScoreMap(initialSoftAgreedScores);
        setSoftAppraiserCommentMap(initialSoftAppraiserComments);

        setAppraiseecomment(data?.appraiseecomment || "");
        setAppraisercomment(data?.appraisercomment || "");
      }
    } catch (error) {
      console.error("Error fetching appraisal:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appraisalid) fetchAppraisal();
  }, [appraisalid]);

  // The raw appraisalstatus stays "notopen" until something explicitly
  // flips it - AppraisalList.js only derives "yettoattend" for its own
  // list/routing display once the start date has arrived and every
  // progress-review checkpoint is closed, but never persists that back to
  // the backend. Re-deriving the same "due" check here means an appraisee
  // who navigated in because the list said "Yet to Attend" actually gets
  // the editable form instead of a read-only view of their own unstarted
  // appraisal.
  const reviewDates = appraisal?.contractprogressreviewdates || [];
  const isAttendDue =
    appraisal?.appraisalstatus === "notopen" &&
    Boolean(appraisal?.startdate) &&
    !dayjs(appraisal.startdate).startOf("day").isAfter(dayjs().startOf("day")) &&
    (reviewDates.length === 0 || reviewDates.every((item) => item.completed));
  const effectiveStatus = isAttendDue ? "yettoattend" : appraisal?.appraisalstatus;
  const isView = effectiveStatus && effectiveStatus !== "yettoattend";
  // Once the appraiser has reviewed and closed this appraisal, their scores,
  // remarks and overall comment are part of the record too - not just the
  // appraisee's own self-assessment.
  const isClosed = appraisal?.appraisalstatus === "closed";

  const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: isClosed ? 8 : 5 } : {});
  const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const buildGroupedRows = (groupedRows) => {
    const rows = [];
    Object.entries(groupedRows).forEach(([perspective, items]) => {
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      items.forEach((row) => rows.push({ ...row, key: rowKeyOf(perspective, row.sno) }));
    });
    return rows;
  };

  const columns = [
    {
      title: "S.No",
      key: "sno",
      width: 80,
      onCell: fullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">
            {record.perspective === "Rollover"
              ? "Rollover Responsibility from Previous Appraisal"
              : record.perspective}
          </div>
        ) : (
          record.sno
        ),
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 120, onCell: hiddenCell },
    {
      title: "Performance Results / Comments",
      key: "performanceresults",
      onCell: hiddenCell,
      render: (_, record) => {
        const key = rowKeyOf(record.perspective, record.sno);
        return isView ? (
          resultsMap[key] || "-"
        ) : (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={resultsMap[key]}
            onChange={(e) => setResultsMap((prev) => ({ ...prev, [key]: e.target.value }))}
            status={showErrors && !String(resultsMap[key] || "").trim() ? "error" : undefined}
          />
        );
      },
    },
    {
      title: "Self score",
      key: "selfscore",
      width: 110,
      onCell: hiddenCell,
      render: (_, record) => {
        const key = rowKeyOf(record.perspective, record.sno);
        return isView ? (
          scoreMap[key] ?? "-"
        ) : (
          <Select
            options={SCORE_OPTIONS}
            value={scoreMap[key]}
            onChange={(val) => setScoreMap((prev) => ({ ...prev, [key]: val }))}
            style={{ width: "100%" }}
            status={showErrors && typeof scoreMap[key] !== "number" ? "error" : undefined}
          />
        );
      },
    },
    ...(isClosed
      ? [
          {
            title: "Appraiser score",
            key: "appraiserscore",
            width: 110,
            onCell: hiddenCell,
            render: (_, record) => appraiserScoreMap[rowKeyOf(record.perspective, record.sno)] ?? "-",
          },
          {
            title: "Agreed score",
            key: "agreedscore",
            width: 110,
            onCell: hiddenCell,
            render: (_, record) => agreedScoreMap[rowKeyOf(record.perspective, record.sno)] ?? "-",
          },
          {
            title: "Appraiser Remarks",
            key: "appraiserremarks",
            onCell: hiddenCell,
            render: (_, record) => appraiserRemarksMap[rowKeyOf(record.perspective, record.sno)] || "-",
          },
        ]
      : []),
  ];

  const grouped = groupByPerspective(
    (appraisal?.contractperspectives || []).filter(
      (item) => item.perspective !== "Individual (Soft Skills)"
    )
  );
  const rolloverRows = appraisal?.contractrolloverresponsibilities || [];
  const combinedGrouped = { ...grouped };
  if (rolloverRows.length > 0) {
    combinedGrouped.Rollover = rolloverRows.map((row) => ({ ...row, perspective: "Rollover" }));
  }

  const averageOf = (values) => {
    const nums = values.filter((val) => typeof val === "number");
    if (!nums.length) return null;
    return Math.round((nums.reduce((sum, val) => sum + val, 0) / nums.length) * 10) / 10;
  };
  const overallAverage = averageOf(
    Object.entries(scoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const softSkillsAverage = averageOf(Object.values(softSkillsScoreMap));
  // Appraiser's and agreed averages only exist once the appraiser has
  // actually reviewed (isClosed) - shown alongside the self average so the
  // appraisee sees the full picture, not just their own score, once closed.
  const appraiserOverallAverage = averageOf(
    Object.entries(appraiserScoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const agreedOverallAverage = averageOf(
    Object.entries(agreedScoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const softAppraiserAverage = averageOf(Object.values(softAppraiserScoreMap));
  const softAgreedAverage = averageOf(Object.values(softAgreedScoreMap));

  const softSkillsColumns = [
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
    {
      title: "What went well/ Major achievement",
      key: "wentwell",
      render: (_, row) =>
        isView ? (
          softSkillsWentWellMap[row.objectives] || "-"
        ) : (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={softSkillsWentWellMap[row.objectives]}
            onChange={(e) =>
              setSoftSkillsWentWellMap((prev) => ({ ...prev, [row.objectives]: e.target.value }))
            }
            status={
              showErrors && !String(softSkillsWentWellMap[row.objectives] || "").trim()
                ? "error"
                : undefined
            }
          />
        ),
    },
    {
      title: "What can be improved / Challenges",
      key: "improved",
      render: (_, row) =>
        isView ? (
          softSkillsImprovedMap[row.objectives] || "-"
        ) : (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={softSkillsImprovedMap[row.objectives]}
            onChange={(e) =>
              setSoftSkillsImprovedMap((prev) => ({ ...prev, [row.objectives]: e.target.value }))
            }
            status={
              showErrors && !String(softSkillsImprovedMap[row.objectives] || "").trim()
                ? "error"
                : undefined
            }
          />
        ),
    },
    {
      title: "Self score",
      key: "selfscore",
      width: 110,
      render: (_, row) =>
        isView ? (
          softSkillsScoreMap[row.objectives] ?? "-"
        ) : (
          <Select
            options={SCORE_OPTIONS}
            value={softSkillsScoreMap[row.objectives]}
            onChange={(val) =>
              setSoftSkillsScoreMap((prev) => ({ ...prev, [row.objectives]: val }))
            }
            style={{ width: "100%" }}
            status={showErrors && typeof softSkillsScoreMap[row.objectives] !== "number" ? "error" : undefined}
          />
        ),
    },
    ...(isClosed
      ? [
          {
            title: "Appraiser score",
            key: "appraiserscore",
            width: 110,
            render: (_, row) => softAppraiserScoreMap[row.objectives] ?? "-",
          },
          {
            title: "Agreed score",
            key: "agreedscore",
            width: 110,
            render: (_, row) => softAgreedScoreMap[row.objectives] ?? "-",
          },
          {
            title: "Appraiser Comment",
            key: "appraisercomment",
            render: (_, row) => softAppraiserCommentMap[row.objectives] || "-",
          },
        ]
      : []),
  ];

  // The "KPI Planning" tab shows the original plan (Planning Assumptions /
  // Comments); a completed checkpoint's review tab shows the same rows with
  // that checkpoint's own Achievements / Corrective actions instead, read
  // from progressupdates[activeReviewTab] (each checkpoint keeps its own
  // entry there, set by the appraiser's Reviewing Progress screen).
  const isReviewTab = activeReviewTab !== "planning";
  const planningColSpan = isReviewTab ? 6 : 5;
  const planningFullSpanCell = (record) => (record.isGroupHeader ? { colSpan: planningColSpan } : {});
  const planningHiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: planningFullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">
            {record.perspective === "Rollover"
              ? "Rollover Responsibility from Previous Appraisal"
              : record.perspective}
          </div>
        ) : (
          record.sno
        ),
    },
    {
      title: "Measurement Dimensions",
      dataIndex: "measurementdimension",
      key: "measurementdimension",
      onCell: planningHiddenCell,
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: planningHiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: planningHiddenCell },
    ...(isReviewTab
      ? [
          {
            title: "Achievements / Progress to date",
            key: "achievements",
            onCell: planningHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.achievements || "-",
          },
          {
            title: "Emphasis / Corrective actions",
            key: "correctiveactions",
            onCell: planningHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.correctiveactions || "-",
          },
        ]
      : [
          {
            title: "Planning Assumptions / Comments",
            dataIndex: "comments",
            key: "comments",
            onCell: planningHiddenCell,
            render: (val) => val || "-",
          },
        ]),
  ];
  const planningGrouped = groupByPerspective(appraisal?.contractperspectives);
  const modalRolloverRows = appraisal?.contractrolloverresponsibilities || [];
  if (modalRolloverRows.length > 0) {
    planningGrouped.Rollover = modalRolloverRows.map((row) => ({ ...row, perspective: "Rollover" }));
  }
  const reviewTabItems = [
    { key: "planning", label: "KPI Planning" },
    ...(appraisal?.contractprogressreviewdates || [])
      .filter((item) => item.completed)
      .map((item) => ({ key: item.label, label: `${item.label} Review` })),
  ];

  const validateForm = () => {
    const planningRows = Object.values(combinedGrouped).flat();
    const planningIncomplete = planningRows.some((row) => {
      const key = rowKeyOf(row.perspective, row.sno);
      return !String(resultsMap[key] || "").trim() || typeof scoreMap[key] !== "number";
    });
    const softSkillsRows = appraisal?.contractsoftskillsassessment || [];
    const softSkillsIncomplete = softSkillsRows.some(
      (row) =>
        !String(softSkillsWentWellMap[row.objectives] || "").trim() ||
        !String(softSkillsImprovedMap[row.objectives] || "").trim() ||
        typeof softSkillsScoreMap[row.objectives] !== "number"
    );
    if (planningIncomplete || softSkillsIncomplete || !String(appraiseecomment || "").trim()) {
      setShowErrors(true);
      message.error("Please fill in all fields before submitting.");
      return false;
    }
    setShowErrors(false);
    return true;
  };

  const onSubmit = () => {
    if (!validateForm()) return;
    Modal.confirm({
      title: "Submit your self-assessment? This can't be changed afterwards.",
      icon: null,
      centered: true,
      okText: "Yes, Submit",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        setSubmitLoading(true);
        try {
          const keys = new Set([...Object.keys(resultsMap), ...Object.keys(scoreMap)]);
          const scoring = Array.from(keys).map((key) => {
            const [perspective, sno] = key.split("-");
            return {
              perspective,
              sno,
              performanceresults: resultsMap[key],
              selfscore: scoreMap[key],
            };
          });
          const softSkillsKeys = new Set([
            ...Object.keys(softSkillsWentWellMap),
            ...Object.keys(softSkillsImprovedMap),
            ...Object.keys(softSkillsScoreMap),
          ]);
          const softskillsscoring = Array.from(softSkillsKeys).map((objectives) => ({
            objectives,
            wentwell: softSkillsWentWellMap[objectives],
            improved: softSkillsImprovedMap[objectives],
            selfscore: softSkillsScoreMap[objectives],
          }));
          const { code } = await CALL_API(
            `performance-kpi-management/attend/${appraisalid}`,
            "patch",
            { scoring, softskillsscoring, appraiseecomment }
          );
          if (code === STATUS_CODE.SUCCESS) {
            setPopupOpen(true);
            setTimeout(() => {
              setPopupOpen(false);
              history.push("/performance", { id: "appraisal", subTab: "mine" });
            }, 1500);
          }
        } catch (error) {
          console.error("Error submitting self-assessment:", error);
        } finally {
          setSubmitLoading(false);
        }
      },
    });
  };

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={() => history.push("/performance", { id: "appraisal", subTab: "mine" })}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">{isClosed ? "Closed" : "Attending Appraisal"}</p>
        </div>

        <h6>Appraisal Form{isClosed ? " (Closed)" : ""}</h6>

        <div className="text-end mb-2">
          <TextLink onClick={() => setClassificationOpen(true)}>Score Classification</TextLink>
        </div>

        <Table
          columns={columns}
          dataSource={buildGroupedRows(combinedGrouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(overallAverage !== null || (isClosed && (appraiserOverallAverage !== null || agreedOverallAverage !== null))) && (
          <div className="text-end mb-2">
            {overallAverage !== null && (
              <span className="average-score-box me-2">Average Score: {overallAverage}</span>
            )}
            {isClosed && appraiserOverallAverage !== null && (
              <span className="average-score-box me-2">Appraiser Average: {appraiserOverallAverage}</span>
            )}
            {isClosed && agreedOverallAverage !== null && (
              <span className="average-score-box">Agreed Average: {agreedOverallAverage}</span>
            )}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="perspective-title m-0">Individual (soft skills) Assessment</div>
          <TextLink onClick={() => setClassificationOpen(true)}>Score Classification</TextLink>
        </div>
        <Table
          columns={softSkillsColumns}
          dataSource={appraisal?.contractsoftskillsassessment || []}
          pagination={false}
          rowKey={(record) => `softskills-${record.objectives}`}
        />

        {(softSkillsAverage !== null || (isClosed && (softAppraiserAverage !== null || softAgreedAverage !== null))) && (
          <div className="text-end mb-2">
            {softSkillsAverage !== null && (
              <span className="average-score-box me-2">Average Score: {softSkillsAverage}</span>
            )}
            {isClosed && softAppraiserAverage !== null && (
              <span className="average-score-box me-2">Appraiser Average: {softAppraiserAverage}</span>
            )}
            {isClosed && softAgreedAverage !== null && (
              <span className="average-score-box">Agreed Average: {softAgreedAverage}</span>
            )}
          </div>
        )}

        {(appraisal?.contracttrainingdevelopmentplan || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <p className="lite-text">
              Based on the strengths and limitations identified in the previous review, indicate the
              work experience and/or training suggested during the plan period.
            </p>
            <Table
              columns={buildTrainingColumns(isClosed)}
              dataSource={appraisal.contracttrainingdevelopmentplan}
              pagination={false}
              rowKey={(record) => `training-${record.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">Appraisee's Comment</div>
        {isView ? (
          <p>{appraiseecomment || "-"}</p>
        ) : (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={appraiseecomment}
            onChange={(e) => setAppraiseecomment(e.target.value)}
            status={showErrors && !String(appraiseecomment || "").trim() ? "error" : undefined}
          />
        )}

        {isClosed && (
          <>
            <div className="perspective-title mt-4">Appraiser's Comment</div>
            <p>{appraisercomment || "-"}</p>
          </>
        )}

        <UserFloatingButton
          setIsViewKpi={setKpiViewOpen}
          bottompossition={isView ? "20px" : "80px"}
          icon={userFloatCycle}
        />

        {!isView && (
          <div className="footer">
            <ButtonOutlined onClick={() => history.push("/performance", { id: "appraisal", subTab: "mine" })}>
              Cancel
            </ButtonOutlined>
            <ButtonFilled type="primary" loading={submitLoading} onClick={onSubmit}>
              Submit
            </ButtonFilled>
          </div>
        )}
      </Spin>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Submitted"
      />

      <Modal
        title="Score Classification"
        open={classificationOpen}
        onCancel={() => setClassificationOpen(false)}
        footer={null}
        centered
      >
        {SCORE_CLASSIFICATION.map((item) => (
          <div
            key={item.value}
            style={{ background: item.color, padding: "12px 16px", marginBottom: 4 }}
          >
            {item.value} - {item.label}
          </div>
        ))}
      </Modal>

      <Modal
        title="KPI Planning & Progress Reviews"
        open={kpiViewOpen}
        onCancel={() => setKpiViewOpen(false)}
        footer={null}
        width={1100}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        centered
      >
        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={24} md={12}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(appraisal?.startdate)}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">KPI defined by</div>
            <div className="text-bold">
              {appraisal?.kpidefinedby === "Self"
                ? `Appraisee : ${appraisal?.fullname || "-"}`
                : `Appraiser : ${appraisal?.kpidefinedby || "-"}`}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Period</div>
            <div className="text-bold">{appraisal?.period || "-"}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Progress Review</div>
            {(appraisal?.contractprogressreviewdates || []).map((item) => (
              <div className="text-bold" key={item.label}>
                {item.label} – {formatDate(item.date)}
              </div>
            ))}
          </Col>
        </Row>

        <Tabs activeKey={activeReviewTab} onChange={setActiveReviewTab} items={reviewTabItems} />

        <div className="perspective-title">Planning</div>
        <p className="lite-text">
          Objectives or other developmental activities to be undertaken by Appraisee during the
          appraisal period.
        </p>
        <Table
          columns={planningColumns}
          dataSource={buildGroupedRows(planningGrouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(appraisal?.contractsoftskillsassessment || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Individual (soft skills) Assessment</div>
            <Table
              columns={[
                { title: "Objectives", dataIndex: "objectives", key: "objectives" },
                { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
              ]}
              dataSource={appraisal.contractsoftskillsassessment}
              pagination={false}
              rowKey={(row) => `modal-softskills-${row.objectives}`}
            />
          </>
        )}

        {(appraisal?.contracttrainingdevelopmentplan || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <Table
              columns={buildTrainingColumns(isClosed)}
              dataSource={appraisal.contracttrainingdevelopmentplan}
              pagination={false}
              rowKey={(row) => `modal-training-${row.sno}`}
            />
          </>
        )}
      </Modal>
    </Wrap>
  );
};

export default AttendContract;
